import { useState } from "react";
import { ChevronLeft } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Input } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/input";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/projects/Admin_Bite_Balance/bite-balance/src/hooks/use-toast";

const Goals = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}");

  // Get preferred weight unit from preferences
  const preferredWeightUnit =
    localStorage.getItem("preferredWeightUnit") || "kg";

  const [formData, setFormData] = useState({
    weightLossGoal: userProfile.weightLossGoal || "Not Sure",
    weightLossReason: userProfile.weightLossReason || "",
    targetWeight: userProfile.targetWeight || "",
    activityLevel: userProfile.activityLevel || "Moderately active",
    dietType: userProfile.dietType || "Classic",
    intermittentFasting:
      userProfile.intermittentFasting || "No — I eat throughout the day",
    fastingWindow:
      userProfile.fastingWindow || "16:8 (Fast 16 hrs / Eat 8 hrs)",
  });

  const handleSave = () => {
    const updatedProfile = { ...userProfile, ...formData };
    localStorage.setItem("userProfile", JSON.stringify(updatedProfile));
    toast({
      title: "Goals Updated",
      description: "Your goals have been successfully updated.",
    });
    navigate("/profile");
  };

  const weightLossOptions = ["Yes", "No", "Not Sure"];
  const weightLossReasons = [
    "To improve blood sugar / manage diabetes",
    "Doctor recommended it",
    "General health / feel better",
    "To look or feel more confident",
    "Other",
  ];
  const activityLevels = [
    "Sedentary",
    "Lightly active",
    "Moderately active",
    "Very active",
  ];
  const dietTypes = [
    "Classic",
    "Vegetarian",
    "Vegan",
    "Pescatarian",
    "Low Carb / Keto",
  ];
  const fastingOptions = [
    "Yes — I follow a fasting window",
    "Sometimes / Not sure",
    "No — I eat throughout the day",
  ];
  const fastingWindows = [
    "16:8 (Fast 16 hrs / Eat 8 hrs)",
    "18:6",
    "14:10",
    "My eating window varies",
  ];

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={() => navigate("/profile")}
            className="rounded-full p-2 transition-colors hover:bg-muted"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-bold text-foreground">
            Goals & Preferences
          </h1>
          <div className="w-10" />
        </div>

        {/* Weight Loss Goal */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-4 font-semibold text-foreground">
            Weight Loss Goal
          </h3>
          <div className="grid grid-cols-3 gap-2">
            {weightLossOptions.map(option => (
              <button
                key={option}
                onClick={() =>
                  setFormData({ ...formData, weightLossGoal: option })
                }
                className={`rounded-xl border-2 p-3 transition-all ${
                  formData.weightLossGoal === option
                    ? "border-primary bg-primary/10 font-medium text-primary"
                    : "border-border hover:border-primary/50"
                }`}
              >
                {option}
              </button>
            ))}
          </div>

          {formData.weightLossGoal === "Yes" && (
            <>
              <div className="mt-4 border-t border-border pt-4">
                <h4 className="mb-3 text-sm font-semibold text-foreground">
                  What's your main reason?
                </h4>
                <div className="space-y-2">
                  {weightLossReasons.map(reason => (
                    <button
                      key={reason}
                      onClick={() =>
                        setFormData({ ...formData, weightLossReason: reason })
                      }
                      className={`w-full rounded-xl border-2 p-3 text-left text-sm transition-all ${
                        formData.weightLossReason === reason
                          ? "border-primary bg-primary/10 font-medium text-primary"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4 border-t border-border pt-4">
                <h4 className="mb-3 text-sm font-semibold text-foreground">
                  Target Weight ({preferredWeightUnit === "kg" ? "Kg" : "Lbs"})
                  - Optional
                </h4>
                <Input
                  type="number"
                  placeholder={
                    preferredWeightUnit === "kg"
                      ? "Target weight in Kg"
                      : "Target weight in Lbs"
                  }
                  value={formData.targetWeight}
                  onChange={e =>
                    setFormData({ ...formData, targetWeight: e.target.value })
                  }
                  className="h-12"
                />
              </div>
            </>
          )}
        </Card>

        {/* Activity Level */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-4 font-semibold text-foreground">Activity Level</h3>
          <div className="space-y-2">
            {activityLevels.map(level => (
              <button
                key={level}
                onClick={() =>
                  setFormData({ ...formData, activityLevel: level })
                }
                className={`w-full rounded-xl border-2 p-3 text-left transition-all ${
                  formData.activityLevel === level
                    ? "border-primary bg-primary/10 font-medium text-primary"
                    : "border-border hover:border-primary/50"
                }`}
              >
                {level}
              </button>
            ))}
          </div>
        </Card>

        {/* Diet Type */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-4 font-semibold text-foreground">Diet Type</h3>
          <div className="space-y-2">
            {dietTypes.map(diet => (
              <button
                key={diet}
                onClick={() => setFormData({ ...formData, dietType: diet })}
                className={`w-full rounded-xl border-2 p-3 text-left transition-all ${
                  formData.dietType === diet
                    ? "border-primary bg-primary/10 font-medium text-primary"
                    : "border-border hover:border-primary/50"
                }`}
              >
                {diet}
              </button>
            ))}
          </div>
        </Card>

        {/* Intermittent Fasting */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-4 font-semibold text-foreground">
            Intermittent Fasting
          </h3>
          <div className="space-y-2">
            {fastingOptions.map(option => (
              <button
                key={option}
                onClick={() =>
                  setFormData({ ...formData, intermittentFasting: option })
                }
                className={`w-full rounded-xl border-2 p-3 text-left transition-all ${
                  formData.intermittentFasting === option
                    ? "border-primary bg-primary/10 font-medium text-primary"
                    : "border-border hover:border-primary/50"
                }`}
              >
                {option}
              </button>
            ))}
          </div>

          {formData.intermittentFasting ===
            "Yes — I follow a fasting window" && (
            <div className="mt-4 border-t border-border pt-4">
              <h4 className="mb-3 text-sm font-semibold text-foreground">
                Fasting Window
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {fastingWindows.map(window => (
                  <button
                    key={window}
                    onClick={() =>
                      setFormData({ ...formData, fastingWindow: window })
                    }
                    className={`rounded-xl border-2 p-3 text-sm transition-all ${
                      formData.fastingWindow === window
                        ? "border-primary bg-primary/10 font-medium text-primary"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    {window}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Save Button */}
        <Button onClick={handleSave} className="h-12 w-full rounded-2xl">
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default Goals;
